// Shared amenities catalog — keys stored on the listing as `amenities: string[]`
// Each item: { key, label } grouped by category.
import {
  ChefHat, Tv, BedDouble, Bath, Wifi, Trees, Waves,
  ShieldCheck, Accessibility, Car, Baby, PawPrint,
} from "lucide-react";

export const AMENITY_CATEGORIES = [
  {
    key: "kitchen", label: "Cuisine", icon: ChefHat,
    items: [
      { key: "kitchen_full", label: "Cuisine équipée" },
      { key: "fridge", label: "Réfrigérateur" },
      { key: "freezer", label: "Congélateur" },
      { key: "oven", label: "Four" },
      { key: "microwave", label: "Micro-ondes" },
      { key: "stove", label: "Plaques de cuisson" },
      { key: "dishwasher", label: "Lave-vaisselle" },
      { key: "coffee_machine", label: "Machine à café" },
      { key: "kettle", label: "Bouilloire" },
      { key: "toaster", label: "Grille-pain" },
      { key: "blender", label: "Blender" },
      { key: "dishware", label: "Vaisselle" },
      { key: "wine_glasses", label: "Verres à vin" },
      { key: "cooking_utensils", label: "Ustensiles de cuisine" },
      { key: "spices", label: "Épices" },
      { key: "oil_vinegar", label: "Huile / vinaigre" },
    ],
  },
  {
    key: "living", label: "Salon", icon: Tv,
    items: [
      { key: "tv", label: "TV" },
      { key: "smart_tv", label: "Smart TV" },
      { key: "netflix", label: "Netflix" },
      { key: "disney_plus", label: "Disney+" },
      { key: "molotov_tv", label: "Molotov TV" },
      { key: "sofa", label: "Canapé" },
      { key: "ac", label: "Climatisation" },
      { key: "heating", label: "Chauffage" },
      { key: "fireplace", label: "Cheminée" },
    ],
  },
  {
    key: "bedroom", label: "Chambre", icon: BedDouble,
    items: [
      { key: "linens", label: "Draps fournis" },
      { key: "pillows", label: "Oreillers" },
      { key: "duvets", label: "Couettes" },
      { key: "baby_cot", label: "Lit bébé" },
      { key: "travel_cot", label: "Lit parapluie" },
      { key: "blackout_blinds", label: "Stores occultants" },
      { key: "hangers", label: "Cintres" },
      { key: "wardrobe", label: "Dressing" },
    ],
  },
  {
    key: "bathroom", label: "Salle de bain", icon: Bath,
    items: [
      { key: "towels", label: "Serviettes" },
      { key: "hairdryer", label: "Sèche-cheveux" },
      { key: "shampoo", label: "Shampoing" },
      { key: "shower_gel", label: "Gel douche" },
      { key: "toilet_paper", label: "Papier toilette" },
      { key: "washer", label: "Machine à laver" },
      { key: "dryer", label: "Sèche-linge" },
      { key: "drying_rack", label: "Étendoir" },
      { key: "iron", label: "Fer à repasser" },
    ],
  },
  {
    key: "internet", label: "Internet & bureau", icon: Wifi,
    items: [
      { key: "wifi", label: "Wi-Fi" },
      { key: "fiber", label: "Fibre" },
      { key: "desk", label: "Bureau" },
      { key: "office_chair", label: "Chaise de bureau" },
    ],
  },
  {
    key: "outdoor", label: "Extérieur", icon: Trees,
    items: [
      { key: "terrace", label: "Terrasse" },
      { key: "garden", label: "Jardin" },
      { key: "bbq", label: "Barbecue" },
      { key: "outdoor_lounge", label: "Salon extérieur" },
      { key: "sun_loungers", label: "Transats" },
      { key: "sea_view", label: "Vue mer" },
      { key: "country_view", label: "Vue campagne" },
      { key: "balcony", label: "Balcon" },
    ],
  },
  {
    key: "pool", label: "Piscine / bien-être", icon: Waves,
    items: [
      { key: "private_pool", label: "Piscine privée" },
      { key: "heated_pool", label: "Piscine chauffée" },
      { key: "jacuzzi", label: "Jacuzzi" },
      { key: "sauna", label: "Sauna" },
      { key: "spa", label: "Spa" },
    ],
  },
  {
    key: "safety", label: "Sécurité", icon: ShieldCheck,
    items: [
      { key: "smoke_detector", label: "Détecteur de fumée" },
      { key: "co_detector", label: "Détecteur de CO2" },
      { key: "fire_extinguisher", label: "Extincteur" },
      { key: "first_aid_kit", label: "Trousse de secours" },
      { key: "outdoor_camera", label: "Caméra extérieure" },
      { key: "alarm", label: "Alarme" },
    ],
  },
  {
    key: "accessibility", label: "Accessibilité", icon: Accessibility,
    items: [
      { key: "pmr_access", label: "Accès PMR" },
      { key: "elevator", label: "Ascenseur" },
      { key: "accessible_shower", label: "Douche accessible" },
      { key: "step_free", label: "Accès sans marche" },
    ],
  },
  {
    key: "parking", label: "Parking", icon: Car,
    items: [
      { key: "free_parking", label: "Parking gratuit" },
      { key: "garage", label: "Garage" },
      { key: "ev_charger", label: "Recharge voiture électrique" },
    ],
  },
  {
    key: "family", label: "Famille", icon: Baby,
    items: [
      { key: "high_chair", label: "Chaise haute" },
      { key: "kids_toys", label: "Jeux enfants" },
      { key: "kids_dishware", label: "Vaisselle enfant" },
    ],
  },
  {
    key: "pets", label: "Animaux", icon: PawPrint,
    items: [
      { key: "pets_allowed", label: "Animaux acceptés" },
      { key: "pet_bowls", label: "Gamelles" },
      { key: "enclosed_garden", label: "Jardin clos" },
    ],
  },
];

// Flat map for quick label lookup
export const AMENITY_LABELS = AMENITY_CATEGORIES.reduce((acc, cat) => {
  cat.items.forEach((it) => { acc[it.key] = it.label; });
  return acc;
}, {});

export const BADGES = {
  coup_de_coeur: { label: "Coup de cœur", className: "bg-[#7A1535] text-white" },
  super_hote: { label: "Super hôte", className: "bg-[#0A0A0A] text-white" },
  vue_mer: { label: "Vue mer", className: "bg-[#1B2A49] text-white" },
  nouveau: { label: "Nouveau", className: "bg-[#C9A14A] text-[#0A0A0A]" },
};
